import React, { useEffect, useState } from "react";
import QuestionBankListing from "./QuestionBankListing";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
  setIsTableLoading,
} from "src/redux/slices/QuestionBankSlice";
import AddQuestionBankFormWrapper from "../Add/AddQuestionBankFormWrapper";
import EditQuestionBankFormWrapper from "../Edit/EditQuestionBankFormWrapper";
import {
  useGetAllQuestionsWithPaginationQuery,
  useDeleteQuestionsMutation,
} from "src/services/QuestionBankServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { showToast } from "src/utils/showToaster";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
type Props = {};

const QuestionBankListingWrapper = (props: Props) => {
  const dispatch = useDispatch();

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteQuestion] = useDeleteQuestionsMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.questionBank);

  const { data, isLoading, isFetching } = useGetAllQuestionsWithPaginationQuery(
    {
      limit: rowsPerPage,
      searchValue: searchValue,
      params: ["question"],
      page: page,
      filterBy: [],
      dateFilter: {},
      orderBy: "createdAt",
      orderByValue: -1,
      isPaginationRequired: true,
    }
  );

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);
  const getActionOptions = (questionId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(questionId);
          setIsOpenEditForm(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.QUESTION,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Question ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteQuestion({ id: questionId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.QUESTION,
      },
    ];
  };

 const ComplexityClasses = (level: string) => {
    switch (level) {
      case "SUPER_EASY":
        return {
          label: "Super Easy",
          class: "bg-lime-600",
        };
      case "EASY":
        return {
          label: "Easy",
          class: "bg-green-800",
        };
      case "MEDIUM":
        return {
          label: "Medium",
          class: "bg-amber-500",
        };
      case "HARD":
        return {
          label: "Hard",
          class: "bg-red-700",
        };
    }
  };

  const columns: columnTypes[] = [
    {
      field: "title",
      headerName: "TITLE",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.title} </div>
      ),
    },

    {
      field: "complexity",
      headerName: "COMPLEXITY",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="p-2 capitalize">
          <span
            className={`${
              ComplexityClasses(row?.complexity)?.class
            } px-3 py-1 rounded-md text-xs text-white`}
          >
            {ComplexityClasses(row?.complexity)?.label}
          </span>
        </div>
      ),
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div>
          {row?.tags?.map((el: any, ind: any) => (
            <span className="text-[11px] font-semibold">
              {row?.tags?.length === ind + 1 ? el : `${el} ,`}{" "}
            </span>
          ))}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <QuestionBankListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddQuestionBankFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditQuestionBankFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          questionId={selectedId}
        />
      ) : null}
    </div>
  );
};

export default QuestionBankListingWrapper;
